import { useState } from 'react';
import { useShowToast } from '@/hooks';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { atsJobDetailsApiActions } from '@/store/atsJobDetails';
import { getRejectionEmailFormData } from '@/utils/getEmailFormConfig';
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { MinusCircle } from '@kula-ai/icons/outline/MinusCircle';
import { useDispatch, useSelector } from 'react-redux';
import { RejectModal } from '@/pages/ats-job-details/tabs/JobPipeline/components';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useRejectApplicationMutation } from '@/store/query/ats-applications';
import { selectUsers } from '@/store/users/selectors';
import { selectSettings, selectCurrUser } from '@/store/bootstrap/selectors';
import AddToHRISModal from '@/pages/hris/AddToHrisModal';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useKFlags } from '@/hooks/use-k-flags';
import { AssociateReqModal } from '@/pages/requisitions/associate-req/AssociateReqModal';
import { RemoveRequisitionConfirmModal } from '@/pages/requisitions/associate-req/RemoveRequisitionConfirmModal';
import { ATS_MILESTONES } from '@/ui-library/components/constants';
import { useRejection } from '@/hooks/useRejection';
// import { CreateProjectDrawer } from '@/pages/projects/components/project-modal/create-project-drawer';

export const MoveToStage = ({
  applicationData,
  refetch = () => {},
  textProps = {},
  text = 'Move to stage',
  buttonProps = {},
  containerRef = null,
  showRejectAsButton = false,
  triggerReference,
  isRejected = false,
}) => {
  const { validateRejectionEmailPayload } = useRejection();
  const { getInterviewPlan, currentJob } = useSelector(getAtsJobCreate);
  const { data: stages } = getInterviewPlan || {};
  const stagesList = applicationData?.ats_job_stages || stages;
  const stagesToShow = stagesList?.filter((stage) => !stage?.deleted);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [pendingMoveStageId, setPendingMoveStageId] = useState(null);
  const [pendingRemoveReqStageId, setPendingRemoveReqStageId] = useState(null);
  const rejectModalActions = useDisclosure();
  const integrationsModalAction = useDisclosure();
  const associateReqModalActions = useDisclosure();
  const removeReqConfirmModalActions = useDisclosure();
  const toast = useShowToast();
  const dispatch = useDispatch();
  const [rejectApplication] = useRejectApplicationMutation();

  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();
  const { feReq, feSurvey } = useKFlags();

  const users = useSelector(selectUsers);
  const settings = useSelector(selectSettings);
  const currUser = useSelector(selectCurrUser);
  const jobHasRequisitions =
    currentJob?.data?.basic_info?.requisitions[0]?.role_name;
  const isRequisitionsEnabled =
    (feReq &&
      currUser?.requisition?.enabled &&
      (jobHasRequisitions || currentJob?.data?.switched_to_new_req)) ??
    false;
  const { ats_job } = applicationData || {};
  const { job_status } = ats_job || {};

  const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
    job_status
  );

  //TODO: Implement Create-Project Drawer
  // const createProjectActions = useDisclosure();
  const clickHandler = (stageId) => {
    setSelectedStageId(stageId);

    // Check if moving FROM hired stage with requisition (and no ats_offer)
    const hiredStageId = stages?.find(
      (i) => i?.ats_job_milestone?.name === ATS_MILESTONES.HIRED
    )?.id;

    const isMovingFromHiredStage =
      applicationData?.job_stage_id === hiredStageId;
    const hasRequisition = applicationData?.requisition?.id;
    const hasAtsOffer = applicationData?.ats_offer?.id;

    if (
      isMovingFromHiredStage &&
      isRequisitionsEnabled &&
      hasRequisition &&
      !hasAtsOffer &&
      stageId !== hiredStageId
    ) {
      // Show confirmation modal to remove requisition
      setPendingRemoveReqStageId(stageId);
      removeReqConfirmModalActions?.onOpen();
      return;
    }

    handleClick(stageId);
  };

  const getTriggerReference = (applicationData) => {
    return applicationData?.ats_job_stages?.some(
      (stage) => stage?.ats_job_milestone?.name === 'Hired'
    )
      ? 'application'
      : 'table';
  };

  const handleClick = async (stageId, requisitionCodes = null) => {
    // Skip if stage is already selected
    if (stageId === applicationData?.job_stage_id) return;

    const isHiredStage = Boolean(
      applicationData?.ats_job_stages?.some(
        (stage) =>
          stage?.ats_job_milestone?.name === 'Hired' && stage?.id === stageId
      )
    );

    const hiredStageId = stages?.find(
      (i) => i?.ats_job_milestone?.name === ATS_MILESTONES.HIRED
    )?.id;

    const hasRequisition = applicationData?.requisition?.id;

    // Check if we need to show requisition modal
    if (
      hiredStageId === stageId &&
      isRequisitionsEnabled &&
      !hasRequisition &&
      !requisitionCodes
    ) {
      setPendingMoveStageId(stageId);
      associateReqModalActions?.onOpen();
      return;
    }

    const allIntegrations =
      settings?.account?.account_integrations?.hris_integrations || [];

    const pushed = applicationData?.pushed_to_hris || [];

    const unpushed = allIntegrations.filter(
      (i) => i.status === 'active' && !pushed.includes(i.type)
    );

    try {
      setActionLoading(true);

      // Build request config with optional requisition_codes
      const requestConfig = {
        to_stage_id: stageId,
        ...(requisitionCodes && { requisition_code: requisitionCodes }),
      };

      // Make API request to move to new stage
      const response = await dispatch(
        atsJobDetailsApiActions('moveToStage', {
          pathParams: { applicationId: applicationData?.id },
          requestConfig,
          isApi: true,
        })
      );

      // Handle response
      if (response?.errors?.length) {
        toast({ title: 'Moving to new stage failed', status: 'error' });
      } else {
        toast({ title: 'Candidate stage updated' });

        const shouldOpenHRISModal =
          unpushed.length > 0 &&
          (isHiredStage || response?.data?.status === 'hired');

        if (shouldOpenHRISModal) {
          const originalOnClose = integrationsModalAction?.onClose;
          integrationsModalAction.onClose = () => {
            if (triggerReference === 'table') refetch();
            originalOnClose?.();
          };
          integrationsModalAction?.onOpen();
          if (triggerReference === 'application') {
            refetch();
          }
        } else {
          // Normal case: refetch immediately
          refetch();
        }
      }
    } catch (error) {
      // Empty catch block preserved as in original
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssociateRequisitions = async (requisitions) => {
    associateReqModalActions?.onClose();

    // Extract requisition codes
    const requisitionCodes = Array.isArray(requisitions)
      ? requisitions.map((req) => req.code).filter(Boolean)
      : requisitions?.code
      ? [requisitions.code]
      : [];

    if (requisitionCodes.length > 0) {
      const reqCount = requisitionCodes.length;
      toast({
        title: `Requisition${reqCount > 1 ? 's' : ''} associated successfully`,
        status: 'success',
      });
    }

    if (pendingMoveStageId) {
      await handleClick(pendingMoveStageId, requisitionCodes);
      setPendingMoveStageId(null);
    }
  };

  const handleRemoveRequisitionConfirm = async () => {
    removeReqConfirmModalActions?.onClose();

    if (pendingRemoveReqStageId) {
      await handleClick(pendingRemoveReqStageId);
      setPendingRemoveReqStageId(null);
    }
  };

  const handleReject = async (values) => {
    try {
      setActionLoading(true);
      const { sendRejectEmail } = values;

      // Since the validation is common across all rejection scenarios, kept the logic useRejection hook here.
      if (!validateRejectionEmailPayload(sendRejectEmail, values || {})) {
        setActionLoading(false);
        return;
      }

      const selectedProjectId = values.addToProject
        ? values.selectedProject
        : '';

      let payload = getRejectionEmailFormData({
        values,
        sendRejectEmail,
        feSurvey,
        processFromUserPayload,
        feSharedEmails,
        users,
      });

      if (selectedProjectId) {
        payload.append('project_id', selectedProjectId);
      }
      const response = await rejectApplication({
        applicationId: applicationData?.id,
        payload,
      });

      if (response?.error?.length) {
        toast({
          title: 'Rejecting the candidate failed',
          status: 'error',
          description: response?.error?.[0],
        });
      } else {
        toast({ title: 'Candidate rejected successfully' });
        rejectModalActions?.onClose();
        refetch();
      }
    } catch (err) {
      console.error('MoveToStage.jsx:handleReject', err);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Flex gap="6px">
        {!isRejected && isJobUpdateAllowedStatus && (
          <Menu>
            <MenuButton
              height="32px"
              variant="primary"
              as={Button}
              rightIcon={<Icon as={ChevronDownIcon} />}
              data-test-id="move-to-stage-button"
              {...buttonProps}
            >
              <TruncatedTextWithTooltip
                color="inherit"
                {...textProps}
                text={text}
                enableTooltip={true}
              />
            </MenuButton>
            <Portal containerRef={containerRef}>
              <MenuList maxH="400px" overflowY="auto" zIndex="5">
                {stagesToShow?.map((stage, idx) => {
                  const isSelected =
                    stage?.id === applicationData?.job_stage_id;
                  return (
                    <MenuItem
                      className="menu-wrapper-16"
                      alignItems="center"
                      onClick={() => clickHandler(stage?.id)}
                      key={idx}
                    >
                      <Flex justifyContent="space-between" flex="1">
                        <TruncatedTextWithTooltip
                          color={isSelected && 'ats-primary'}
                          fontSize="sm"
                          fontWeight={isSelected && 600}
                          text={stage?.name}
                          enableTooltip={true}
                          maxWidth="350px"
                        />
                        {isSelected && (
                          <Icon as={CheckIcon} color="ats-primary" />
                        )}
                      </Flex>
                    </MenuItem>
                  );
                })}
                {!showRejectAsButton && (
                  <>
                    <MenuDivider />
                    <MenuItem
                      gap="12px"
                      onClick={() => rejectModalActions?.onOpen()}
                      fontSize="sm"
                    >
                      <Icon as={MinusCircle} color="ats-negative" />
                      Reject candidate
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Portal>
          </Menu>
        )}
        {showRejectAsButton && !isRejected && (
          <Button
            variant="secondary"
            bg="error-200"
            color="base-white"
            maxH="34px"
            _hover={{
              bg: 'error-100',
            }}
            _active={{}}
            onClick={() => rejectModalActions?.onOpen()}
            border="none"
            data-test-id="candidate-detail-reject-button"
          >
            Reject
          </Button>
        )}
      </Flex>
      {integrationsModalAction?.isOpen && (
        <AddToHRISModal
          modalActions={integrationsModalAction}
          applicationData={applicationData}
          selectedStageId={selectedStageId}
          triggerReference={getTriggerReference(applicationData)}
          moveOnlyToHired={handleClick}
          refetch={refetch}
        />
      )}
      {rejectModalActions?.isOpen && (
        <RejectModal
          rejectModalActions={rejectModalActions}
          handleSubmit={handleReject}
          isLoading={actionLoading}
          actionApplication={applicationData}
          isProjectCreationAllowed={false}
          // onProjectClick={() => createProjectActions?.onOpen()}
        />
      )}
      {associateReqModalActions?.isOpen && (
        <AssociateReqModal
          isOpen={associateReqModalActions?.isOpen}
          onClose={() => {
            associateReqModalActions?.onClose();
            setPendingMoveStageId(null);
          }}
          onAssociate={handleAssociateRequisitions}
          multiSelect={false}
        />
      )}
      {removeReqConfirmModalActions?.isOpen && (
        <RemoveRequisitionConfirmModal
          isOpen={removeReqConfirmModalActions?.isOpen}
          onClose={() => {
            removeReqConfirmModalActions?.onClose();
            setPendingRemoveReqStageId(null);
          }}
          onConfirm={handleRemoveRequisitionConfirm}
          isLoading={actionLoading}
          applicationData={applicationData}
        />
      )}
      {/* {createProjectActions?.isOpen && (
        <CreateProjectDrawer
          modalActions={createProjectActions}
          setCreatedProjectData={() => {}}
          mode="create"
        />
      )} */}
    </>
  );
};
